"use client";

import React from "react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { SlidingNumber } from "./sliding-number";
import { cn } from "@/lib/utils";

interface QuantityCounterProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
  containerClassName?: string;
}

export function QuantityCounter({
  value,
  onChange,
  min = 1,
  max,
  disabled = false,
  className = "",
  size = "md",
  containerClassName = "",
}: QuantityCounterProps) {
  const handleDecrease = () => {
    if (disabled) return;
    const newValue = Math.max(min, value - 1);
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const handleIncrease = () => {
    if (disabled) return;
    const newValue = max !== undefined ? Math.min(max, value + 1) : value + 1;
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const canDecrease = !disabled && value > min;
  const canIncrease = !disabled && (max === undefined || value < max);

  const sizeClasses = {
    sm: {
      container: "px-2 py-1.5 md:px-3 md:py-2",
      icon: "size-3",
      text: "min-w-[1rem] md:min-w-[2rem]",
    },
    md: {
      container: "px-5",
      icon: "size-4",
      text: "min-w-[2rem]",
    },
    lg: {
      container: "px-6",
      icon: "size-5",
      text: "min-w-[2.5rem]",
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full bg-[#F3F4F6]",
        size === "sm" ? "border-border-title/50 border-[.8px]" : "border-border-primmary/50 border-[.8px]",
        currentSize.container,
        containerClassName
      )}
    >
      <MinusIcon
        onClick={handleDecrease}
        className={cn(
          "text-icon-title/60 hover:text-icon-title cursor-pointer transition-colors",
          currentSize.icon,
          !canDecrease && "cursor-not-allowed opacity-50",
          disabled && "cursor-not-allowed opacity-50"
        )}
        title='Decrease quantity'
      />
      <span
        className={cn(
          "text-text-title flex items-center justify-center text-center select-none",
          currentSize.text,
          className
        )}
      >
        <SlidingNumber value={value} />
      </span>
      <PlusIcon
        onClick={handleIncrease}
        className={cn(
          "text-icon-title/60 hover:text-icon-title cursor-pointer transition-colors",
          currentSize.icon,
          !canIncrease && "cursor-not-allowed opacity-50",
          disabled && "cursor-not-allowed opacity-50"
        )}
        title='Increase quantity'
      />
    </div>
  );
}
